import React, { useState } from 'react';
import { DateTime } from 'luxon';
import { EmailCampaign } from '../core/_models_email';
import { useAuth } from '../AuthContext';

interface EmailViewModalProps {
  campaign: EmailCampaign | null;
  isOpen: boolean;
  onClose: () => void;
}

const EmailViewModal: React.FC<EmailViewModalProps> = ({
  campaign,
  isOpen,
  onClose
}) => {
  const { currentUser } = useAuth();
  const [showSignerDetails, setShowSignerDetails] = useState(false);
  const [showCampaignDetails, setShowCampaignDetails] = useState(false);
  const [showAiAnalysis, setShowAiAnalysis] = useState(false);
  const [showAiPromptResponse, setShowAiPromptResponse] = useState(false);
  
  if (!isOpen || !campaign) return null;

  const getLeadScoreBadgeClass = (score: number) => {
    if (score >= 80) return 'badge-success';
    if (score >= 60) return 'badge-warning';
    if (score >= 40) return 'badge-info';
    return 'badge-light';
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
      case 'sent':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'sent':
        return 'Sent';
      default:
        return status;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="transition-all duration-300 fixed inset-0 bg-gray-900 opacity-25 z-40" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b">
            <div className="flex items-center gap-4">
              <h3 className="text-xl font-semibold text-gray-900">View Email Campaign</h3>
              <div className="flex items-center gap-2">
                <span className={`badge ${getLeadScoreBadgeClass(campaign.lead_score)}`}>
                  Score: {campaign.lead_score}
                </span>
                <span className={`badge ${getStatusBadgeClass(campaign.status)}`}>
                  {getStatusText(campaign.status)}
                </span>
                {campaign.is_dry_run && (
                  <span className="badge badge-light-warning">Dry Run</span>
                )}
                {campaign.lead_score_breakdown?.interested_in_pre_need > 0 && (
                  <span className="badge badge-light-primary">Pre-Need</span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ki-filled ki-cross text-2xl"></i>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Compact single-line summary */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setShowSignerDetails(!showSignerDetails)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">From:</span>
                  <span className="font-medium">{campaign.signer_info?.name || 'Unknown'}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">For:</span>
                  <span className="font-medium">{campaign.deceased_info?.name || campaign.service_name || 'Unknown'}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">{campaign.signer_info?.email || 'No email'}</span>
                </div>
                <i className={`ki-filled ${showSignerDetails ? 'ki-up' : 'ki-down'} text-gray-400`}></i>
              </div>
              
              {/* Expandable details */}
              {showSignerDetails && (
                <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold text-gray-700 text-xs mb-2 uppercase">Signer Details</h5>
                    <p className="text-sm"><strong>{campaign.signer_info?.name || 'Unknown'}</strong></p>
                    <p className="text-sm text-gray-600">{campaign.signer_info?.email || 'No email'}</p>
                    {campaign.signer_info?.phone && (
                      <p className="text-sm text-gray-600">{campaign.signer_info.phone}</p>
                    )}
                    {campaign.signer_info?.relationship && (
                      <p className="text-sm text-gray-600">Relationship: {campaign.signer_info.relationship}</p>
                    )}
                    {campaign.signer_info?.address && (
                      <p className="text-sm text-gray-600">{campaign.signer_info.address}</p>
                    )}
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-700 text-xs mb-2 uppercase">Service Details</h5>
                    <p className="text-sm"><strong>{campaign.deceased_info?.name || campaign.service_name || 'Unknown'}</strong></p>
                    {campaign.service_unique_identifier && (
                      <p className="text-sm text-gray-600">ID: {campaign.service_unique_identifier}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Compact Campaign Details */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 cursor-pointer hover:bg-gray-100 transition-colors" onClick={() => setShowCampaignDetails(!showCampaignDetails)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">Lead Score:</span>
                  <span className={`badge ${getLeadScoreBadgeClass(campaign.lead_score)}`}>
                    {campaign.lead_score}
                  </span>
                  <span className="text-gray-400">•</span>
                  {campaign.sent_at ? (
                    <>
                      <span className="text-gray-600">Sent:</span>
                      <span className="font-medium" title={DateTime.fromISO(campaign.sent_at).toFormat('LLL d, yyyy \'at\' h:mm a')}>
                        {DateTime.fromISO(campaign.sent_at).toRelative()}
                      </span>
                    </>
                  ) : campaign.reviewed_at ? (
                    <>
                      <span className="text-gray-600">Reviewed:</span>
                      <span className="font-medium" title={DateTime.fromISO(campaign.reviewed_at).toFormat('LLL d, yyyy \'at\' h:mm a')}>
                        {DateTime.fromISO(campaign.reviewed_at).toRelative()}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-gray-600">Created:</span>
                      <span className="font-medium" title={DateTime.fromISO(campaign.created_at).toFormat('LLL d, yyyy \'at\' h:mm a')}>
                        {DateTime.fromISO(campaign.created_at).toRelative()}
                      </span>
                    </>
                  )}
                </div>
                <i className={`ki-filled ${showCampaignDetails ? 'ki-up' : 'ki-down'} text-gray-400`}></i>
              </div>
              
              {/* Expandable campaign details */}
              {showCampaignDetails && (
                <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold text-gray-700 text-xs mb-2 uppercase">Timeline</h5>
                    <p className="text-sm text-gray-600">
                      Created: <span title={DateTime.fromISO(campaign.created_at).toFormat('LLL d, yyyy \'at\' h:mm a')}>
                        {DateTime.fromISO(campaign.created_at).toRelative()}
                      </span>
                    </p>
                    {campaign.reviewed_at && (
                      <p className="text-sm text-gray-600">
                        Reviewed: <span title={DateTime.fromISO(campaign.reviewed_at).toFormat('LLL d, yyyy \'at\' h:mm a')}>
                          {DateTime.fromISO(campaign.reviewed_at).toRelative()}
                        </span>
                      </p>
                    )}
                    {campaign.sent_at && (
                      <p className="text-sm text-gray-600">
                        Sent: <span title={DateTime.fromISO(campaign.sent_at).toFormat('LLL d, yyyy \'at\' h:mm a')}>
                          {DateTime.fromISO(campaign.sent_at).toRelative()}
                        </span>
                      </p>
                    )}
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-700 text-xs mb-2 uppercase">Lead Score Breakdown</h5>
                    {Object.entries(campaign.lead_score_breakdown).length > 0 ? (
                      Object.entries(campaign.lead_score_breakdown).map(([key, value]) => (
                        <p key={key} className="text-sm text-gray-600">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: {typeof value === 'number' ? value.toFixed(2) : value}
                        </p>
                      ))
                    ) : (
                      <p className="text-sm text-gray-600">No breakdown available</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* AI Edit Analysis Section - Only show if email was edited */}
            {campaign.was_edited_by_user && (
              <div className="bg-blue-50 rounded-lg mb-4">
                <div 
                  className="p-3 cursor-pointer hover:bg-blue-100 transition-colors rounded-lg" 
                  onClick={() => setShowAiAnalysis(!showAiAnalysis)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ki-filled ki-technology text-blue-600"></i>
                      <span className="text-sm font-medium text-blue-900">AI Edit Analysis</span>
                      <span className="badge badge-light-info badge-sm">Email was edited before sending</span>
                    </div>
                    <i className={`ki-filled ${showAiAnalysis ? 'ki-up' : 'ki-down'} text-blue-600`}></i>
                  </div>
                </div>
                
                {/* Expandable AI analysis */}
                {showAiAnalysis && (
                  <div className="p-3 pt-0">
                    <div className="border-t border-blue-200 pt-3">
                      <h5 className="font-semibold text-blue-900 text-xs mb-2 uppercase">Changes Made to AI-Generated Content</h5>
                      <div className="bg-white rounded p-3">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {(() => {
                            if (typeof campaign.edit_summary === 'string') {
                              return campaign.edit_summary;
                            } else if (campaign.edit_summary && typeof campaign.edit_summary === 'object') {
                              // Handle old format - extract meaningful information
                              const summary = campaign.edit_summary as any;
                              const changes = [];
                              
                              if (summary.subject_changed) {
                                changes.push('Subject line was modified');
                              }
                              
                              if (summary.content_changes) {
                                if (summary.content_changes.lines_added > 0) {
                                  changes.push(`${summary.content_changes.lines_added} line(s) added`);
                                }
                                if (summary.content_changes.added_phone_number) {
                                  changes.push('Contact information was added');
                                }
                                if (summary.content_changes.length_change > 50) {
                                  changes.push('Significant content was added');
                                } else if (summary.content_changes.length_change < -50) {
                                  changes.push('Content was shortened');
                                }
                              }
                              
                              return changes.length > 0 
                                ? changes.join('. ') + '.'
                                : 'Email content was modified before sending.';
                            }
                            return 'Email content was modified before sending.';
                          })()}
                        </p>
                      </div>
                      {campaign.original_ai_subject && campaign.email_subject !== campaign.original_ai_subject && (
                        <div className="mt-3">
                          <h6 className="font-medium text-xs text-gray-600 mb-1">Subject Line Changed</h6>
                          <div className="text-xs space-y-1">
                            <p className="text-gray-500">
                              <span className="font-medium">Original:</span> {campaign.original_ai_subject}
                            </p>
                            <p className="text-gray-700">
                              <span className="font-medium">Final:</span> {campaign.email_subject}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Show AI prompt/response for super super users */}
                    {currentUser?.is_super_super_user && campaign.edit_analysis_data && (
                      <div className="mt-3">
                        <button
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAiPromptResponse(!showAiPromptResponse);
                          }}
                        >
                          <i className={`ki-filled ${showAiPromptResponse ? 'ki-up' : 'ki-down'} text-xs`}></i>
                          View AI Analysis Details
                        </button>
                        {showAiPromptResponse && (
                          <div className="mt-2 space-y-3 text-xs">
                            {campaign.edit_analysis_data.error ? (
                              <div className="bg-red-50 border border-red-200 rounded p-3">
                                <h6 className="font-medium text-red-900 mb-1">Error Generating Analysis</h6>
                                <p className="text-red-700">{campaign.edit_analysis_data.error}</p>
                                <p className="text-red-600 mt-1">Fallback analysis was used.</p>
                              </div>
                            ) : (
                              <>
                                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                                  <h6 className="font-medium text-gray-700 mb-1">AI Prompt</h6>
                                  <div className="bg-white rounded p-2 space-y-2">
                                    <div>
                                      <span className="font-medium text-gray-600">System:</span>
                                      <pre className="whitespace-pre-wrap text-gray-600 mt-1">
                                        {campaign.edit_analysis_data.prompt?.system_prompt}
                                      </pre>
                                    </div>
                                    <div className="border-t pt-2">
                                      <span className="font-medium text-gray-600">User Message:</span>
                                      <pre className="whitespace-pre-wrap text-gray-600 mt-1 max-h-32 overflow-y-auto">
                                        {campaign.edit_analysis_data.prompt?.use_messages?.[0]?.content}
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded p-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <h6 className="font-medium text-green-900">AI Response</h6>
                                    {campaign.edit_analysis_data.model_used && (
                                      <span className="badge badge-light-success badge-sm">
                                        <i className="ki-filled ki-technology text-xs mr-1"></i>
                                        {campaign.edit_analysis_data.model_used}
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-green-700 whitespace-pre-wrap">
                                    {campaign.edit_analysis_data.ai_response}
                                  </p>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Original AI Generation Details for chris@cohoat.tech only */}
            {currentUser?.email === 'chris@cohoat.tech' && campaign.ai_prompt && campaign.ai_response && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-semibold text-gray-700">Original AI Generation Details</h5>
                  <div className="flex items-center gap-3">
                    {campaign.model_used && (
                      <span className="badge badge-light-primary">
                        <i className="ki-filled ki-technology text-xs mr-1"></i>
                        {campaign.model_used}
                      </span>
                    )}
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCampaignDetails(!showCampaignDetails);
                      }}
                    >
                      <i className={`ki-filled ${showCampaignDetails ? 'ki-up' : 'ki-down'} text-xs`}></i>
                      {showCampaignDetails ? 'Hide' : 'Show'} Details
                    </button>
                  </div>
                </div>
                
                {showCampaignDetails && (
                  <div className="space-y-3">
                    <div className="bg-white border border-gray-200 rounded p-3">
                      <h6 className="font-medium text-gray-700 mb-2 text-sm">Original AI Prompt</h6>
                      <pre className="whitespace-pre-wrap text-xs text-gray-600 bg-gray-50 p-2 rounded overflow-auto max-h-64">
                        {campaign.ai_prompt}
                      </pre>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded p-3">
                      <h6 className="font-medium text-gray-700 mb-2 text-sm">Original AI Response</h6>
                      <pre className="whitespace-pre-wrap text-xs text-gray-600 bg-gray-50 p-2 rounded overflow-auto max-h-64">
                        {(() => {
                          try {
                            const parsed = JSON.parse(campaign.ai_response);
                            return JSON.stringify(parsed, null, 2);
                          } catch {
                            return campaign.ai_response;
                          }
                        })()}
                      </pre>
                    </div>
                    
                    <button
                      className="text-xs text-blue-600 hover:text-blue-800"
                      onClick={(e) => {
                        e.stopPropagation();
                        const text = `ORIGINAL AI GENERATION\n\nPrompt:\n${campaign.ai_prompt}\n\nResponse:\n${campaign.ai_response}`;
                        navigator.clipboard.writeText(text);
                      }}
                    >
                      <i className="ki-filled ki-copy text-xs mr-1"></i>
                      Copy to clipboard
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="border rounded">
              <div className="flex justify-between items-center p-3 border-b bg-gray-50">
                <h4 className="font-semibold text-gray-700">Email Content</h4>
                <div className="flex items-center gap-2">
                  {campaign.was_edited_by_user ? (
                    <span className="badge badge-light-success badge-sm">
                      <i className="ki-filled ki-pencil text-xs mr-1"></i>
                      Human Edited
                    </span>
                  ) : (
                    <span className="badge badge-light badge-sm">
                      <i className="ki-filled ki-technology text-xs mr-1"></i>
                      AI Generated
                    </span>
                  )}
                  <span className="text-sm text-gray-500">Read-only</span>
                </div>
              </div>
              
              <div className="p-4">
                <div className="mb-3">
                  <span className="text-sm font-medium text-gray-700">To:</span>
                  <span className="text-sm ml-2">{campaign.signer_info?.email || 'No email'}</span>
                </div>
                <div className="mb-3">
                  <span className="text-sm font-medium text-gray-700">Subject:</span>
                  <span className="text-sm ml-2">{campaign.actual_sent_subject || campaign.email_subject}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Message:</span>
                  <div className="mt-2 text-sm whitespace-pre-wrap bg-gray-50 p-3 rounded">
                    {campaign.actual_sent_content || campaign.final_email_content}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t">
            <button
              onClick={onClose}
              className="btn btn-lg btn-light"
            >
              <i className="ki-filled ki-cross mr-2"></i>
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailViewModal;